/* global trapFocus, removeTrapFocus */

if (!customElements.get('hotspots-image')) {
  class HotspotsImage extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.hoverTimeout = null;
    }

    connectedCallback() {
      this.hotspots = this.querySelectorAll('.hotspot');
      this.attachEventListeners();
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
      }
    }

    attachEventListeners() {
      this.hotspots.forEach(hotspot => {
        hotspot.querySelector('.hotspot__btn').addEventListener('click', this.handleClick);
        hotspot.addEventListener('mouseenter', this.handleMouseEnter);
        hotspot.addEventListener('mouseleave', this.handleMouseLeave);
      });
    }

    isMobile() {
      if (typeof theme !== 'undefined' && theme.mediaMatches) {
        return !theme.mediaMatches.md;
      }
      return window.innerWidth < 768;
    }

    handleClick(evt) {
      const hotspot = evt.target.closest('.hotspot');
      if (!hotspot) return;

      if (hotspot === this.activeHotspot) {
        this.closeActiveHotspot(evt);
      } else {
        if (this.activeHotspot) this.closeActiveHotspot(evt);
        this.activateHotspot(hotspot);
      }
    }

    handleMouseEnter(evt) {
      if (this.isMobile()) return;

      const hotspot = evt.currentTarget;

      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = null;
      }

      if (this.activeHotspot && this.activeHotspot !== hotspot) {
        this.closeActiveHotspot(evt);
      }

      if (this.activeHotspot !== hotspot) {
        this.activateHotspot(hotspot);
      }
    }

    handleMouseLeave(evt) {
      if (this.isMobile()) return;

      const hotspot = evt.currentTarget;

      this.hoverTimeout = setTimeout(() => {
        if (this.activeHotspot === hotspot) {
          this.closeActiveHotspot(evt);
        }
      }, 150);
    }

    handleClickOutside(evt) {
      // If clicking close button, close the card
      if (evt.target.closest('.hotspot__card-close')) {
        this.closeActiveHotspot(evt);
        return;
      }
      
      // Don't close if clicking inside card or on button
      if (evt.target.closest('.hotspot__btn') || evt.target.closest('.hotspot__card')) return;
      
      if (this.activeHotspot) this.closeActiveHotspot(evt);
    }

    handleKeyup(evt) {
      if (evt.key !== 'Escape') return;
      if (this.activeHotspot) this.closeActiveHotspot(evt);
    }

    handleTransitionEnd(evt) {
      if (this.activeHotspot || evt.propertyName !== 'opacity') return;

      // Clear positioning styles AFTER transition completes
      if (this.activeCard) {
        this.activeCard.style.left = '';
        this.activeCard.style.right = '';
        this.activeCard.style.top = '';
        this.activeCard.style.bottom = '';
        this.activeCard.style.marginLeft = '';
        this.activeCard.style.marginRight = '';
        this.activeCard.style.marginTop = '';
        this.activeCard.style.transform = '';
      }

      this.activeCard = null;

      this.removeEventListener('transitionend', this.transitionHandler);
    }

    activateHotspot(hotspot) {
      // Store reference to card BEFORE moving it
      this.activeCard = hotspot.querySelector('.hotspot__card');
      
      // Store original parent so we can move card back later
      this.activeCardParent = this.activeCard.parentElement;
      
      // Set state BEFORE moving card (so querySelector still works)
      this.setHotspotActiveState(hotspot, true);
      
      // Now move card if on mobile
      this.checkCardPosition();
      
      this.classList.remove('overflow-hidden');
      trapFocus(hotspot);

      this.clickOutsideHandler = this.clickOutsideHandler || this.handleClickOutside.bind(this);
      this.keyUpHandler = this.keyUpHandler || this.handleKeyup.bind(this);
      this.resizeHandler = this.resizeHandler || this.closeActiveHotspot.bind(this);

      document.addEventListener('click', this.clickOutsideHandler);
      document.addEventListener('keyup', this.keyUpHandler);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    closeActiveHotspot(evt) {
      if (evt && evt.type !== 'on:debounced-resize') {
        this.transitionHandler = this.transitionHandler || this.handleTransitionEnd.bind(this);
        this.addEventListener('transitionend', this.transitionHandler);
      }

      if (this.activeHotspot) {
        // Move card back to its original parent if it was moved to body
        if (this.activeCard && this.activeCard.classList.contains('hotspot__card--mobile-fixed')) {
          this.activeCardParent.appendChild(this.activeCard);
          this.activeCard.classList.remove('hotspot__card--mobile-fixed');
        }

        this.setHotspotActiveState(this.activeHotspot, false);
        removeTrapFocus();

        if (evt && evt.type === 'on:debounced-resize') {
          // For resize events, clear styles immediately (no transition)
          if (this.activeCard) {
            this.activeCard.style.left = '';
            this.activeCard.style.right = '';
            this.activeCard.style.top = '';
            this.activeCard.style.bottom = '';
            this.activeCard.style.marginLeft = '';
            this.activeCard.style.marginRight = '';
          }
          this.activeCard = null;
          this.activeCardParent = null;
        }
      }

      document.removeEventListener('click', this.clickOutsideHandler);
      document.removeEventListener('keyup', this.keyUpHandler);
    }

    checkCardPosition() {
      // Mobile: Move card to body so position:fixed works
      if (this.isMobile()) {
        document.body.appendChild(this.activeCard);
        this.activeCard.classList.add('hotspot__card--mobile-fixed');
        return;
      }

      // Desktop: Position card left or right based on available space
      const hotspot = this.activeCard.closest('.hotspot');
      const container = this.querySelector('.hotspots-image__media');
      
      if (!hotspot || !container) return;

      const containerRect = container.getBoundingClientRect();
      const hotspotRect = hotspot.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const padding = 20; // Minimum padding from viewport edge
      
      // Calculate hotspot position as percentage of container width
      const hotspotCenterX = hotspotRect.left + hotspotRect.width / 2;
      const positionPercent = (hotspotCenterX - containerRect.left) / containerRect.width * 100;

      // Reset positioning
      this.activeCard.style.left = '';
      this.activeCard.style.right = '';
      this.activeCard.style.transform = '';
      this.activeCard.style.marginLeft = '';
      this.activeCard.style.marginRight = '';
      this.activeCard.style.marginTop = '';

      // Initial positioning based on hotspot position
      if (positionPercent > 50) {
        this.activeCard.style.right = '100%';
        this.activeCard.style.left = 'auto';
        this.activeCard.style.marginRight = '12px';
        this.activeCard.style.marginLeft = '0';
      } else {
        this.activeCard.style.left = '100%';
        this.activeCard.style.right = 'auto';
        this.activeCard.style.marginLeft = '12px';
        this.activeCard.style.marginRight = '0';
      }

      // Check if card overflows viewport and adjust
      let cardRect = this.activeCard.getBoundingClientRect();

      // If card overflows right edge, try left side
      if (cardRect.right > viewportWidth - padding) {
        this.activeCard.style.right = '100%';
        this.activeCard.style.left = 'auto';
        this.activeCard.style.marginRight = '12px';
        this.activeCard.style.marginLeft = '0';
        
        cardRect = this.activeCard.getBoundingClientRect();
        
        // If still overflows (left edge), use fixed mobile-style positioning
        if (cardRect.left < padding) {
          this.positionCardAsOverlay();
          return;
        }
      }
      // If card overflows left edge, try right side
      else if (cardRect.left < padding) {
        this.activeCard.style.left = '100%';
        this.activeCard.style.right = 'auto';
        this.activeCard.style.marginLeft = '12px';
        this.activeCard.style.marginRight = '0';
        
        cardRect = this.activeCard.getBoundingClientRect();
        
        // If still overflows (right edge), use fixed mobile-style positioning
        if (cardRect.right > viewportWidth - padding) {
          this.positionCardAsOverlay();
          return;
        }
      }

      // Check vertical position - if card goes below viewport, move it up
      cardRect = this.activeCard.getBoundingClientRect();
      if (cardRect.bottom > window.innerHeight - padding) {
        this.activeCard.style.top = 'auto';
        this.activeCard.style.bottom = '0';
      }
    }

    positionCardAsOverlay() {
      // On mobile, use fixed overlay
      if (this.isMobile()) {
        document.body.appendChild(this.activeCard);
        this.activeCard.classList.add('hotspot__card--mobile-fixed');
        return;
      }

      // On desktop (or narrow customizer), position card below the pin
      this.activeCard.style.left = '50%';
      this.activeCard.style.right = 'auto';
      this.activeCard.style.top = '100%';
      this.activeCard.style.transform = 'translateX(-50%)';
      this.activeCard.style.marginLeft = '0';
      this.activeCard.style.marginRight = '0';
      this.activeCard.style.marginTop = '12px';
    }

    setHotspotActiveState(hotspot, active) {
      hotspot.classList.toggle('is-active', active);
      hotspot.querySelector('.hotspot__btn').setAttribute('aria-expanded', active);
      
      // Use stored reference instead of querying (card might be moved to body)
      if (this.activeCard) {
        this.activeCard.setAttribute('aria-hidden', !active);
      }

      this.activeHotspot = active ? hotspot : null;
    }
  }

  customElements.define('hotspots-image', HotspotsImage);
}